import { Injectable } from "@angular/core";
import Bugsnag from "@bugsnag/js";
import * as utils from "@apps/patient-core/src/app/utils";
import { BaseAppInitService } from "@apps/patient-core/src/app/services/abstracts/app-init.service";
import { notifyAndLeaveBreadcrumb, setupBugsnag } from "@apps/patient-core/src/app/utils/logging";
import { CacheService } from "@apps/patient-core/src/app/services/cache.service";
import { LocationService } from "@apps/patient-core/src/app/services/location.service";
import { NavigationService } from "@apps/patient-core/src/app/services/navigation.service";
import { NewRelicService } from "@apps/patient-core/src/app/services/new-relic.service";
import { ConciergeService } from "../concierge.service";
import { Constants } from "@apps/concierge-app/constants";
import { BaseAuthService } from "@apps/patient-core/src/app/services/abstracts/auth.service";

@Injectable({
  providedIn: "root",
})
export class ConciergeAppInitService extends BaseAppInitService {
  constructor(
    private _authService: BaseAuthService,
    private _locationService: LocationService,
    private _cacheService: CacheService,
    private _navigationService: NavigationService,
    private _newRelicService: NewRelicService,
    private _conciergeService: ConciergeService
  ) {
    super();
  }
  protected async _doInit() {
    this.checkIfStorageSupported();

    // Automatically hide the app loading spinner if it's not hidden automatically
    utils.checkAppLoadingSpinnerIsRemoved(10);

    /*
      Check if we have previous Concierge data (stage, site ID etc) and restore it
      If we had data which was restored, we need to init for paired because this device has already been paired
    */
    if (await this._conciergeService.restoreConciergeData()) {
      await this._conciergeService.initForPaired();
    } else {
      // If we've not already paired, we need to init for pairing to allow the practice user to pair the device
      await this._conciergeService.initForPairing();
    }

    setupBugsnag(this._conciergeService.deviceId);

    const deviceId = this._authService.getJWT("device_id");
    const practiceId = this._authService.getJWT("practice_id");

    Bugsnag.addMetadata("Device", {
      device_id: deviceId,
      practice_id: practiceId,
      site_id: this._conciergeService.siteId,
      domain: this._locationService.hostname,
    });
    this._newRelicService.setUserId(deviceId);
  }

  public async init() {
    // Initialize the session service first to ensure we have the session cookie set
    await this._authService.init();

    try {
      await this._doInit();
    } catch (error) {
      notifyAndLeaveBreadcrumb("Failed to initialize app", {
        error,
        conciergeData: this._cacheService.getJson(Constants.CONCIERGE_DATA_STORAGE_KEY),
        jwt: this._authService.getJWT(),
      });

      this._navigationService.navigate("/error", {
        state: {
          error: "Failed to initialise the concierge application",
        },
      });
    }
  }
}
